import { useEffect, useRef, useState } from "react";
import { History, X } from "lucide-react";
import { api } from "@/lib/api";

const RECENTS_KEY = "aureus_recent_addresses";
const MAX_RECENTS = 5;

const readRecents = () => {
  try {
    return JSON.parse(localStorage.getItem(RECENTS_KEY)) || [];
  } catch {
    return [];
  }
};

const pushRecent = (label) => {
  try {
    const list = [label, ...readRecents().filter((a) => a !== label)].slice(0, MAX_RECENTS);
    localStorage.setItem(RECENTS_KEY, JSON.stringify(list));
  } catch {}
};

export function AddressInput({ value, onChange, placeholder, testId, className }) {
  const [suggestions, setSuggestions] = useState([]);
  const [recents, setRecents] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const inputRef = useRef(null);
  const debRef = useRef(null);
  const selectedRef = useRef(false);

  useEffect(() => {
    const close = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const showRecents = () => {
    const r = readRecents();
    setSuggestions([]);
    setRecents(r);
    setOpen(r.length > 0);
  };

  const handleFocus = () => {
    if (value.trim().length < 3) showRecents();
    else if (suggestions.length > 0) setOpen(true);
  };

  const handleChange = (e) => {
    const v = e.target.value;
    selectedRef.current = false;
    onChange(v);
    clearTimeout(debRef.current);
    if (v.trim().length < 3) {
      showRecents();
      return;
    }
    debRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/geo/suggest", { params: { q: v } });
        if (selectedRef.current) return;
        setSuggestions(data);
        setOpen(data.length > 0);
      } catch {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 350);
  };

  const select = (label) => {
    selectedRef.current = true;
    clearTimeout(debRef.current);
    pushRecent(label);
    onChange(label);
    setSuggestions([]);
    setOpen(false);
  };

  const clear = () => {
    selectedRef.current = true;
    clearTimeout(debRef.current);
    onChange("");
    setSuggestions([]);
    setOpen(false);
    inputRef.current?.focus();
  };

  const showSuggestionList = open && suggestions.length > 0;
  const showRecentList = open && suggestions.length === 0 && recents.length > 0;

  return (
    <div ref={boxRef} className="relative">
      <input
        ref={inputRef}
        value={value}
        onChange={handleChange}
        onFocus={handleFocus}
        placeholder={placeholder}
        className={className}
        data-testid={testId}
        autoComplete="off"
      />
      {loading && (
        <span
          className={`absolute ${value ? "right-9" : "right-3"} top-1/2 -translate-y-1/2 font-mono-lux text-[9px] uppercase tracking-widest text-zinc-500`}
        >
          …
        </span>
      )}
      {value && (
        <button
          type="button"
          aria-label="Clear address"
          onMouseDown={(e) => e.preventDefault()}
          onClick={clear}
          data-testid={`${testId}-clear`}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-amber-300 transition-colors duration-300"
        >
          <X size={14} strokeWidth={1.5} />
        </button>
      )}
      {showSuggestionList && (
        <ul
          className="absolute z-30 left-0 right-0 mt-1 border border-[#2E3340] bg-[#16181D] shadow-2xl max-h-56 overflow-y-auto"
          data-testid={`${testId}-suggestions`}
        >
          {suggestions.map((s, i) => (
            <li key={i}>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => select(s.label)}
                data-testid={`${testId}-suggestion-${i}`}
                className="w-full text-left px-4 py-3 text-xs font-medium text-zinc-200 hover:bg-[#1E2128] hover:text-amber-200 transition-colors border-b border-[#2E3340]/50 last:border-0"
              >
                {s.label}
              </button>
            </li>
          ))}
        </ul>
      )}
      {showRecentList && (
        <ul
          className="absolute z-30 left-0 right-0 mt-1 border border-[#2E3340] bg-[#16181D] shadow-2xl max-h-56 overflow-y-auto"
          data-testid={`${testId}-recents`}
        >
          <li className="px-4 pt-2.5 pb-1 font-mono-lux text-[9px] font-semibold uppercase tracking-[0.25em] text-zinc-500">
            Recent addresses
          </li>
          {recents.map((r, i) => (
            <li key={i}>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => select(r)}
                data-testid={`${testId}-recent-${i}`}
                className="w-full flex items-center gap-2.5 text-left px-4 py-3 text-xs font-medium text-zinc-200 hover:bg-[#1E2128] hover:text-amber-200 transition-colors border-b border-[#2E3340]/50 last:border-0"
              >
                <History size={12} strokeWidth={1.5} className="shrink-0 text-zinc-500" />
                {r}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
